import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import axios from '../../../utils/axiosConfig';
import { message, Card, Table, Button, Space, Typography, Input, Select, Row, Col, Tag, Modal, Popconfirm, Breadcrumb } from 'antd';
import { 
  CarOutlined, 
  ReloadOutlined, 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  SearchOutlined,
  EnvironmentOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;

const VehicleManagement = () => {
  const { token, currentUser } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [locations, setLocations] = useState([]);
  const [workspaces, setWorkspaces] = useState([]); // eslint-disable-line no-unused-vars
  const [vehicleAssignments, setVehicleAssignments] = useState([]); // eslint-disable-line no-unused-vars
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterLocation, setFilterLocation] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [selectedVehicles, setSelectedVehicles] = useState([]); // eslint-disable-line no-unused-vars
  const [selectedRowKeys, setSelectedRowKeys] = useState([]); // eslint-disable-line no-unused-vars
  const [selectedLocationId, setSelectedLocationId] = useState(null); // eslint-disable-line no-unused-vars
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState(null); // eslint-disable-line no-unused-vars
  const [assignmentNotes, setAssignmentNotes] = useState(''); // eslint-disable-line no-unused-vars
  const [isBulkAssign, setIsBulkAssign] = useState(false); // eslint-disable-line no-unused-vars
  
  // Check if user can assign to workspaces (enterprise/agency/admin)
  const canAssignToWorkspaces = ['superadmin', 'admin', 'agency', 'enterprise'].includes(currentUser?.role);

  const authHeader = useCallback(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);

  // Fetch vehicles directly from Geotab (filtered by admin assignments)
  const fetchVehicles = useCallback(async () => {
    setLoading(true);
    try {
      // First get vehicles assigned to this client by admin
      const assignmentsRes = await axios.get('/api/client-vehicle-assignments/my-vehicles', { headers: authHeader() });
      const assignments = assignmentsRes.data || [];
      
      if (assignments.length === 0) {
        message.info('No vehicles have been assigned to you by the administrator. Please contact support@pathmarkr.com for vehicle assignment.');
        setVehicles([]);
        return;
      }

      // Group assignments by Geotab database
      const databaseGroups = assignments.reduce((acc, assignment) => {
        if (!acc[assignment.geotab_database]) {
          acc[assignment.geotab_database] = [];
        }
        acc[assignment.geotab_database].push(assignment);
        return acc;
      }, {});

      const allTransformedVehicles = [];

      // Fetch vehicles for each Geotab database
      for (const [database, dbAssignments] of Object.entries(databaseGroups)) {
        try {
          const { data } = await axios.get(`/api/integrations/geotab/devices?databases=${database}`, { headers: authHeader() });
          const devices = Array.isArray(data?.devices) ? data.devices : [];

          // Transform and filter vehicles
          const transformedVehicles = devices
            .filter(device => {
              // Only include vehicles that are assigned to this client
              return dbAssignments.some(assignment => assignment.geotab_device_id === device.id);
            })
            .map((device) => {
              // Find the assignment for this vehicle
              const assignment = dbAssignments.find(a => a.geotab_device_id === device.id);
              
              // A vehicle is active if:
              // 1. activeTo is null/undefined, OR
              // 2. activeTo is a far-future date (Geotab uses max date like 2050-01-01 for "no end date")
              const activeTo = device.activeTo;
              let isActive = true; // Default to active
              
              if (activeTo) {
                const activeToDate = new Date(activeTo);
                const now = new Date();
                // If activeTo is in the past, device is inactive
                // If activeTo is in the future (more than 1 year), it's effectively "no end date"
                isActive = activeToDate > now || activeToDate.getFullYear() >= 2030;
              }
              
              return {
                id: device.id,
                name: device.name || device.description || `Vehicle ${device.id}`,
                geotab_device_id: device.id,
                location: device.comment || device.groups?.[0]?.name || '',
                serial_number: device.serialNumber || device.vehicleIdentificationNumber || '',
                license_plate: device.licensePlate || device.licenseState || '',
                vin: device.vehicleIdentificationNumber || '',
                device_type: device.deviceType || '',
                status: isActive ? 'active' : 'inactive',
                active_from: device.activeFrom || null,
                active_to: device.activeTo || null,
                created_at: device.activeFrom || new Date().toISOString(),
                raw_device: device,
                assignment_id: assignment?.id || null,
                assigned_at: assignment?.assigned_at || null,
                geotab_database: database,
                geotab_account_name: assignment?.geotab_account_name || ''
              };
            });

          allTransformedVehicles.push(...transformedVehicles);
        } catch (err) {
          console.error(`Error fetching vehicles for database ${database}:`, err);
        }
      }

      setVehicles(allTransformedVehicles);
      
      if (allTransformedVehicles.length > 0) {
        message.success(`Loaded ${allTransformedVehicles.length} assigned vehicle(s) from Geotab`);
      }
    } catch (err) {
      console.error('Error fetching vehicles:', err);
      message.error('Failed to fetch vehicles from Geotab');
    } finally {
      setLoading(false);
    }
  }, [authHeader]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch locations for assignment
  const fetchLocations = useCallback(async () => {
    try {
      const { data } = await axios.get('/api/locations', { headers: authHeader() });
      setLocations(Array.isArray(data) ? data : (data.items || []));
    } catch (err) {
      console.error('Error fetching locations:', err);
    }
  }, [authHeader]);

  // Fetch workspaces for assignment (enterprise/agency users)
  const fetchWorkspaces = useCallback(async () => {
    if (!canAssignToWorkspaces) return;
    try {
      const { data } = await axios.get('/api/vehicle-tracking/workspaces', { headers: authHeader() });
      setWorkspaces(Array.isArray(data) ? data : (data.items || []));
    } catch (err) {
      console.error('Error fetching workspaces:', err);
    }
  }, [authHeader, canAssignToWorkspaces]);

  // Fetch vehicle assignments
  const fetchAssignments = useCallback(async () => {
    try {
      const { data } = await axios.get('/api/vehicle-tracking/assignments', { headers: authHeader() });
      setVehicleAssignments(Array.isArray(data) ? data : (data.items || []));
    } catch (err) {
      console.error('Error fetching vehicle assignments:', err);
    }
  }, [authHeader, setVehicleAssignments]);

  useEffect(() => {
    fetchVehicles();
    fetchLocations();
    fetchWorkspaces();
    fetchAssignments();
  }, [fetchVehicles, fetchLocations, fetchWorkspaces, fetchAssignments]);

  // Map vehicles with their assigned location from assignments
  const vehiclesWithAssignments = useMemo(() => {
    return vehicles.map(vehicle => {
      // Find assignment for this vehicle
      const assignment = vehicleAssignments.find(a => a.geotab_device_id === vehicle.geotab_device_id);
      // Find workspace name if assigned
      const assignedWorkspace = assignment ? workspaces.find(w => w.id === assignment.workspace_id) : null;
      return {
        ...vehicle,
        location: assignedWorkspace ? assignedWorkspace.name : '',
        assignment_id: assignment?.id || null
      };
    });
  }, [vehicles, vehicleAssignments, workspaces]);

  // Filter vehicles
  const filteredVehicles = useMemo(() => {
    return vehiclesWithAssignments.filter(vehicle => {
      const matchesSearch = !searchQuery || 
        vehicle.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        vehicle.geotab_device_id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        vehicle.serial_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        vehicle.license_plate?.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesLocation = filterLocation === 'all' || 
        vehicle.location === filterLocation ||
        (filterLocation === 'unassigned' && (!vehicle.location || vehicle.location === 'N/A'));
      
      const matchesStatus = filterStatus === 'all' || vehicle.status === filterStatus;
      
      return matchesSearch && matchesLocation && matchesStatus;
    });
  }, [vehiclesWithAssignments, searchQuery, filterLocation, filterStatus]);

  // Unique locations for filter (based on workspaces)
  const locationOptions = useMemo(() => {
    const workspaceNames = workspaces.map(w => w.name).filter(Boolean);
    const uniqueLocations = ['all', 'unassigned', ...Array.from(new Set(workspaceNames))];
    
    return uniqueLocations.map(loc => {
      if (loc === 'all') return { value: 'all', label: 'All Locations' };
      if (loc === 'unassigned') return { value: 'unassigned', label: 'Unassigned' };
      return { value: loc, label: loc };
    });
  }, [workspaces]);

  // Handle delete vehicle (show warning for Geotab vehicles)
  const handleDelete = async (vehicle) => {
    Modal.confirm({
      title: 'Delete Vehicle',
      content: `Are you sure you want to delete "${vehicle.name}"? This is a Geotab vehicle and will only be removed from this view.`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: () => {
        // Remove from local state only (Geotab vehicles can't be deleted via API)
        setVehicles(prev => prev.filter(v => v.id !== vehicle.id));
        message.success('Vehicle removed from view');
      }
    });
  };

  // Open edit modal (read-only for Geotab vehicles)
  const openEditModal = (vehicle) => {
    const activeFromDate = vehicle.active_from ? new Date(vehicle.active_from).toLocaleDateString() : null;
    const activeToDate = vehicle.active_to ? new Date(vehicle.active_to).toLocaleDateString() : 'Currently Active';
    
    const details = [
      { label: 'Name', value: vehicle.name },
      { label: 'Geotab ID', value: vehicle.geotab_device_id },
      { label: 'Serial Number', value: vehicle.serial_number },
      { label: 'VIN', value: vehicle.vin },
      { label: 'License Plate', value: vehicle.license_plate },
      { label: 'Device Type', value: vehicle.device_type },
      { label: 'Location', value: vehicle.location },
      { label: 'Status', value: vehicle.status === 'active' ? 'Active (In Use)' : 'Inactive (Disabled)' },
      { label: 'Active From', value: activeFromDate },
      { label: 'Active To', value: vehicle.status === 'active' ? 'Currently Active' : activeToDate }
    ].filter(item => item.value); // Only show fields with values

    Modal.info({
      title: 'Vehicle Details',
      width: 600,
      content: (
        <div>
          {details.map((item, index) => (
            <p key={index}>
              <strong>{item.label}:</strong> {item.value}
            </p>
          ))}
          {locations.length > 0 && (
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
              <Button 
                type="primary" 
                icon={<EnvironmentOutlined />}
                onClick={() => {
                  Modal.destroyAll();
                  openAssignLocationModal(vehicle);
                }}
              >
                Assign to Location
              </Button>
            </div>
          )}
        </div>
      ),
      okText: 'Close'
    });
  };

  // Open assign location modal for single vehicle
  const openAssignLocationModal = (vehicle) => {
    setSelectedVehicle(vehicle);
    setSelectedVehicles([vehicle]);
    setIsBulkAssign(false);
    setAssignModalVisible(true);
  };

  // Open bulk assign modal for multiple vehicles
  // eslint-disable-next-line no-unused-vars
  const openBulkAssignModal = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Please select at least one vehicle');
      return;
    }
    const selected = vehicles.filter(v => selectedRowKeys.includes(v.id));
    setSelectedVehicles(selected);
    setSelectedVehicle(null);
    setIsBulkAssign(true);
    setAssignModalVisible(true);
  };

  // Handle location assignment (single or bulk)
  const handleAssignLocation = async () => {
    if (!selectedLocationId) {
      message.warning('Please select a location');
      return;
    }
    
    const vehiclesToAssign = isBulkAssign ? selectedVehicles : [selectedVehicle];
    
    try {
      let successCount = 0;
      let failCount = 0;
      
      for (const vehicle of vehiclesToAssign) {
        try {
          await axios.post('/api/vehicle-tracking/assign', {
            geotab_device_id: vehicle.geotab_device_id,
            location_id: selectedLocationId || null,
            workspace_id: selectedLocationId || null,
            notes: assignmentNotes || null
          }, { headers: authHeader() });
          successCount++;
        } catch (err) {
          console.error(`Error assigning vehicle ${vehicle.name}:`, err);
          failCount++;
        }
      }
      
      if (successCount > 0) {
        message.success(`${successCount} vehicle(s) assigned successfully`);
      }
      if (failCount > 0) {
        message.warning(`${failCount} vehicle(s) failed to assign`);
      }
      
      setAssignModalVisible(false);
      setSelectedVehicle(null);
      setSelectedVehicles([]);
      setSelectedRowKeys([]);
      setSelectedLocationId(null);
      setSelectedWorkspaceId(null);
      setAssignmentNotes('');
      setIsBulkAssign(false);
      fetchAssignments();
    } catch (err) {
      console.error('Error assigning vehicles:', err);
      message.error('Failed to assign vehicles');
    }
  };

  // Row selection configuration for table
  // eslint-disable-next-line no-unused-vars
  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys) => {
      setSelectedRowKeys(newSelectedRowKeys);
    },
  };

  // Open create modal (disabled for Geotab-only view)
  const openCreateModal = () => {
    Modal.info({
      title: 'Add Vehicle',
      content: 'This view shows vehicles directly from Geotab. To add new vehicles, please add them in your Geotab account first.',
      okText: 'OK'
    });
  };

  // Table columns
  const columns = [
    {
      title: 'Vehicle Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          <CarOutlined style={{ color: '#1890ff' }} />
          <span>{text || `Vehicle ${record.id}`}</span>
        </Space>
      ),
      sorter: (a, b) => (a.name || '').localeCompare(b.name || '')
    },
    {
      title: 'Geotab ID',
      dataIndex: 'geotab_device_id',
      key: 'geotab_device_id',
      render: (text) => text || '-'
    },
    {
      title: 'Serial/VIN',
      dataIndex: 'serial_number',
      key: 'serial_number',
      render: (text, record) => text || record.vin || '-'
    },
    {
      title: 'License Plate',
      dataIndex: 'license_plate',
      key: 'license_plate',
      render: (text) => text || '-'
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
      render: (location) => {
        if (!location || location === 'N/A') return <Tag color="default">Unassigned</Tag>;
        return (
          <Tag color="blue" icon={<EnvironmentOutlined />}>
            {location}
          </Tag>
        );
      },
      filters: locationOptions.slice(1).map(opt => ({ text: opt.label, value: opt.value })),
      onFilter: (value, record) => {
        if (value === 'unassigned') return !record.location || record.location === 'N/A';
        return record.location === value;
      }
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const statusText = status === 'active' ? 'Active' : 'Inactive';
        return (
          <Tag color={status === 'active' ? 'green' : 'red'}>
            {statusText}
          </Tag>
        );
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button 
            type="link" 
            icon={<EditOutlined />} 
            onClick={() => openEditModal(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete this vehicle?"
            description="This action cannot be undone."
            onConfirm={() => handleDelete(record.id)}
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <Breadcrumb items={[{ title: 'Enterprise' }, { title: 'Vehicle Management' }]} />
      
      <div style={{ marginTop: 12, marginBottom: 24 }}>
        <Title level={3} style={{ margin: 0 }}>
          <CarOutlined style={{ marginRight: 8 }} />
          Vehicle Management
        </Title>
        <Text type="secondary" style={{ marginTop: 8, display: 'block' }}>
          Manage your fleet vehicles and assign them to locations
        </Text>
      </div>

      <Card>
        {/* Action Bar */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={8}>
            <Input
              placeholder="Search vehicles..."
              prefix={<SearchOutlined />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={5}>
            <Select
              value={filterLocation}
              onChange={setFilterLocation}
              style={{ width: '100%' }}
              options={locationOptions}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              value={filterStatus}
              onChange={setFilterStatus}
              style={{ width: '100%' }}
              options={[
                { value: 'all', label: 'All Status' },
                { value: 'active', label: 'Active' },
                { value: 'inactive', label: 'Inactive' }
              ]}
            />
          </Col>
          <Col xs={24} sm={24} md={7}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button 
                type={selectedRowKeys.length > 0 ? "primary" : "default"}
                icon={<EnvironmentOutlined />} 
                onClick={selectedRowKeys.length > 0 ? openBulkAssignModal : undefined}
                disabled={selectedRowKeys.length === 0}
              >
                Assign {selectedRowKeys.length > 0 ? `${selectedRowKeys.length} to Location` : "to Location"}
              </Button>
              <Button 
                icon={<ReloadOutlined />} 
                onClick={fetchVehicles}
                loading={loading}
              >
                Refresh
              </Button>
              <Button 
                type="default" 
                icon={<PlusOutlined />} 
                onClick={openCreateModal}
              >
                Vehicle Info
              </Button>
            </Space>
          </Col>
        </Row>

        {/* Summary Stats */}
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col>
            <Tag color="blue">Total: {vehicles.length}</Tag>
          </Col>
          <Col>
            <Tag color="green">Active: {vehicles.filter(v => v.status === 'active').length}</Tag>
          </Col>
          <Col>
            <Tag color="red">Inactive: {vehicles.filter(v => v.status === 'inactive').length}</Tag>
          </Col>
          <Col>
            <Tag color="cyan">Located: {vehicles.filter(v => v.location && v.location !== 'N/A').length}</Tag>
          </Col>
          <Col>
            <Tag color="default">Filtered: {filteredVehicles.length}</Tag>
          </Col>
        </Row>

        {/* Vehicles Table */}
        <Table
          rowSelection={rowSelection}
          columns={columns}
          dataSource={filteredVehicles}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} vehicles`
          }}
        />
      </Card>

      {/* Assign Location/Workspace Modal */}
      <Modal
        title={isBulkAssign 
          ? `Assign ${selectedVehicles.length} Vehicle(s) to Workspace` 
          : "Assign Vehicle to Workspace"
        }
        open={assignModalVisible}
        onCancel={() => {
          setAssignModalVisible(false);
          setSelectedVehicle(null);
          setSelectedVehicles([]);
          setSelectedLocationId(null);
          setSelectedWorkspaceId(null);
          setAssignmentNotes('');
          setIsBulkAssign(false);
        }}
        onOk={handleAssignLocation}
        okText={isBulkAssign ? `Assign ${selectedVehicles.length} Vehicle(s)` : "Assign"}
        width={500}
      >
        {(selectedVehicle || selectedVehicles.length > 0) && (
          <div>
            {isBulkAssign ? (
              <div style={{ marginBottom: 16 }}>
                <Text strong>Selected Vehicles ({selectedVehicles.length}):</Text>
                <div style={{ maxHeight: 120, overflowY: 'auto', marginTop: 8, padding: 8, background: '#f5f5f5', borderRadius: 4 }}>
                  {selectedVehicles.map(v => (
                    <Tag key={v.id} style={{ margin: 2 }}>{v.name}</Tag>
                  ))}
                </div>
              </div>
            ) : (
              <p style={{ marginBottom: 16 }}>
                <strong>Vehicle:</strong> {selectedVehicle?.name}
              </p>
            )}
            
            <div style={{ marginBottom: 16 }}>
              <Text strong>Location:</Text>
              <Select
                style={{ width: '100%', marginTop: 8 }}
                placeholder="Select a location"
                allowClear
                value={selectedLocationId}
                onChange={setSelectedLocationId}
              >
                {workspaces.map(workspace => (
                  <Select.Option key={workspace.id} value={workspace.id}>
                    <EnvironmentOutlined style={{ marginRight: 8 }} />
                    {workspace.name}
                  </Select.Option>
                ))}
              </Select>
            </div>

            <div style={{ marginBottom: 16 }}>
              <Text strong>Notes (optional):</Text>
              <Input.TextArea
                style={{ marginTop: 8 }}
                placeholder="Add any notes about this assignment..."
                value={assignmentNotes}
                onChange={(e) => setAssignmentNotes(e.target.value)}
                rows={2}
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default VehicleManagement;
